import { Card, Col, Image, Row, Tag, Typography } from "antd";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteProduct } from "../../redux/actions/product/deleteProductAction";
import { loadSingleProduct } from "../../redux/actions/product/detailProductAction";
import Loader from "../loader/loader";

const { Text } = Typography;

const detailRow = (label, value) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
      borderBottom: "1px solid #f0f0f0",
      gap: 16,
    }}
  >
    <Text style={{ color: "#8c8c8c", fontSize: 13 }}>{label}</Text>
    <Text strong style={{ fontSize: 13, textAlign: "right" }}>{value ?? "-"}</Text>
  </div>
);

const DetailsProd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const product = useSelector((state) => state.products.product);

  const onDelete = () => {
    try {
      dispatch(deleteProduct(id));
      toast.warning(`Product : ${product.name} is removed`);
      navigate("/product");
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    dispatch(loadSingleProduct(id));
  }, [id, dispatch]);

  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }

  if (!product) {
    return <Loader />;
  }

  const categoryDisplay =
    product.categories?.length > 0
      ? product.categories.map((cat) => cat.name || cat).join(", ")
      : product.product_category?.name || null;
  const currencySymbol =
    product.product_currency?.symbol || product.product_currency?.name || "";
  const priceFormatted =
    product.sale_price != null
      ? `${currencySymbol} ${Number(product.sale_price).toFixed(2)}`
      : null;

  return (
    <div
      style={{
        marginTop: 0,
        padding: "0 16px 24px",
        minHeight: "100%",
        background: "linear-gradient(180deg, #f8f9fa 0%, #fff 120px)",
      }}
    >
      <Card
        bordered={false}
        className="card-custom"
        style={{
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <Row gutter={[32, 24]} align="top">
          {/* Image - Left */}
          <Col xs={24} md={10} lg={8}>
            <div
              style={{
                background: "linear-gradient(145deg, #f0f2f5 0%, #e4e6eb 100%)",
                borderRadius: 12,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                maxWidth: 280,
                aspectRatio: "3/4",
                border: "1px solid #e8e8e8",
                boxShadow: "inset 0 1px 2px rgba(255,255,255,0.8)",
              }}
            >
              {product.imageUrl ? (
                <Image
                  src={product.imageUrl}
                  alt={product.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                  preview={{ mask: "View" }}
                />
              ) : (
                <div
                  style={{
                    color: "#8c8c8c",
                    fontSize: 13,
                    textAlign: "center",
                    padding: 24,
                  }}
                >
                  <span
                    className="bi bi-journal-plus"
                    style={{
                      fontSize: 56,
                      display: "block",
                      marginBottom: 12,
                      color: "#b8b8b8",
                    }}
                  />
                  No Image Available
                </div>
              )}
            </div>
          </Col>

          {/* Details - Right */}
          <Col xs={24} md={14} lg={16}>
            <div style={{ paddingRight: 8 }}>
              {/* Title */}
              <Typography.Title
                level={3}
                style={{
                  marginTop: 0,
                  marginBottom: 8,
                  fontWeight: 700,
                  color: "#1f1f1f",
                  lineHeight: 1.35,
                }}
              >
                {product.name}
              </Typography.Title>

              {/* Author */}
              {product.author && (
                <Text
                  style={{
                    display: "block",
                    marginBottom: 12,
                    color: "#1890ff",
                    fontSize: 14,
                  }}
                >
                  by {product.author}
                </Text>
              )}

              {/* Category tag */}
              {categoryDisplay && (
                <Tag
                  style={{
                    marginBottom: 20,
                    borderRadius: 6,
                    padding: "4px 12px",
                    fontSize: 12,
                    background: "#f0f0f0",
                    color: "#595959",
                    border: "none",
                  }}
                >
                  {categoryDisplay}
                </Tag>
              )}

              {/* Price block */}
              {priceFormatted && (
                <div
                  style={{
                    marginBottom: 24,
                    padding: "16px 0",
                    borderTop: "1px solid #f0f0f0",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      color: "#1f1f1f",
                    }}
                  >
                    {priceFormatted}
                  </Text>
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: 12, marginTop: 4 }}
                  >
                    Sale price
                  </Text>
                </div>
              )}

              {/* Stock hint */}
              {product.quantity != null && (
                <div style={{ marginBottom: 20 }}>
                  <Text
                    style={{
                      color: product.quantity > 0 ? "#52c41a" : "#ff4d4f",
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {product.quantity > 0
                      ? `In stock (${product.quantity} available)`
                      : "Out of stock"}
                  </Text>
                </div>
              )}

              {/* Details section */}
              <div
                style={{
                  background: "#fafafa",
                  borderRadius: 8,
                  padding: "16px 20px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: 12,
                    fontSize: 14,
                    color: "#262626",
                  }}
                >
                  Details
                </Text>
                {detailRow("ISBN", product.isbn)}
                {detailRow("Publisher", product.book_publisher?.name)}
                {detailRow("Category", categoryDisplay)}
                {detailRow("Currency", product.product_currency?.name)}
                {detailRow("Unit type", product.unit_type)}
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default DetailsProd;
